import {
  companySettlementsSelector,
  getObjectId,
  SettlementEntity,
  transportMethod,
  useGetCompanySettlementsBySettlementQuery
} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {ActionIcon, Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {CommentModal} from "../../comment/CommentModal/CommentModal";
import {useEntityCommentModal} from "../../comment/EntityCommentModal/useEntityCommentModal";
import {IconMessagePlus, IconPlus} from "@tabler/icons-react";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {EntityCommentList} from "../../comment/EntityCommentList/EntityCommentList";
import {CompanySettlementDataTable} from "../../data-table/CompanySettlementDataTable/CompanySettlementDataTable";
import {
  CompanySettlementFormModal,
  CompanySettlementFormValues
} from "../../form/CompanySettlementForm/CompanySettlementForm";
import {useDisclosure} from "@mantine/hooks";
import {useCompanySettlementMutation} from "../../../hooks/useCompanySettlementMutation";
import {FormattedActivityPeriodDate} from "../../date/FormattedActivityPeriodDate/FormattedActivityPeriodDate";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {DefaultText} from "@ots-frontend/ots-ui";

export type SettlementDetailsProps = {
  settlement: SettlementEntity
}

export const SettlementDetails = (props: SettlementDetailsProps) => {
  const {settlement} = props;

  const {regionId} = useApp()

  const {t} = useTranslation(["translation", "common"]);

  const {
    opened,
    handlers,
    onSubmit: onCommentSubmit,
    isCreating: isCommentCreating
  } = useEntityCommentModal({
    entity: settlement
  });

  const [truckingFormOpen, truckingFormHandler] = useDisclosure(false)
  const [pipelineFormOpen, pipelineFormHandler] = useDisclosure(false)

  const {
    createCompanySettlement,
    isCreating,
  } = useCompanySettlementMutation()

  const selectCompanySettlements = useMemo(() => {
    return companySettlementsSelector();
  }, []);

  const {companySettlements, isCompanySettlementsLoading} = useGetCompanySettlementsBySettlementQuery(settlement.id, {
    selectFromResult: (result) => ({
      companySettlements: selectCompanySettlements(result),
      isCompanySettlementsLoading: result.isLoading
    })
  });

  const truckingCompanySettlements = useMemo(() => {
    return companySettlements.filter(cs => cs.method === transportMethod.trucking)
  }, [companySettlements]);

  const pipelineCompanySettlements = useMemo(() => {
    return companySettlements.filter(cs => cs.method === transportMethod.pipeline)
  }, [companySettlements]);

  const onCompanySettlementFormSubmit = useCallback((values: CompanySettlementFormValues) => {
    createCompanySettlement({
      settlementId: settlement.id,
      companyId: getObjectId(values.company),
      name: values.name,
      method: values.method.code,
      status: values.status.code,
    }, () => {
      truckingFormHandler.close()
      pipelineFormHandler.close()
    })
  }, [createCompanySettlement, settlement, truckingFormHandler, pipelineFormHandler])

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("common.entity.settlement.profile.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            {settlement.name &&
              <Grid.Col span={12}>
                <Text ta="left" fz="sm" weight={500} mt="md">
                  {t("common.entity.settlement.profile.field.name.label", "Name")}
                </Text>
                <Text ta="left" c="dimmed" fz="xs">
                  {settlement.name}
                </Text>
              </Grid.Col>
            }
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.settlement.profile.field.company.label", "Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={settlement.activityPeriodCompanyName} />
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.settlement.profile.field.companyunit.label", "Unit")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {settlement.activityPeriodCompanyUnitId && <DefaultText value={settlement.activityPeriodCompanyUnitName} />}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.settlement.profile.field.status.label", "Status")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={settlement.status}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.settlement.profile.field.date.label", "Date")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedActivityPeriodDate
                  reportTimeType={settlement.activityPeriodType}
                  fromDate={settlement.activityPeriodFromDttm}
                  toDate={settlement.activityPeriodToDttm}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.settlement.profile.field.type.label", "Period")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={settlement.activityPeriodType}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.settlement.profile.field.category.label", "Category")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={settlement.activityPeriodCategory}/>
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>
              {t("common.entity.settlement.profile.section.trucking", "Trucking Settlement")}
            </Title>
            <HasAccess permissions={["edit"]}>
              <ActionIcon color="green" onClick={truckingFormHandler.open}>
                <IconPlus size={16}/>
              </ActionIcon>
            </HasAccess>
          </Group>

          <Divider pb="sm"/>

          <CompanySettlementDataTable
            withBorder
            records={truckingCompanySettlements}
            fetching={isCompanySettlementsLoading}
          />
        </Card>
      </Grid.Col>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>
              {t("common.entity.settlement.profile.section.pipeline", "Pipeline Settlement")}
            </Title>
            <HasAccess permissions={["edit"]}>
              <ActionIcon color="green" onClick={pipelineFormHandler.open}>
                <IconPlus size={16}/>
              </ActionIcon>
            </HasAccess>
          </Group>

          <Divider pb="sm"/>

          <CompanySettlementDataTable
            withBorder
            records={pipelineCompanySettlements}
            fetching={isCompanySettlementsLoading}
          />
        </Card>
      </Grid.Col>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title
              order={4}>{t("common.entity.settlement.profile.info.section.comments.label", "Comments")}</Title>
            <HasAccess permissions={["edit"]}>
              <ActionIcon color="green" onClick={handlers.open}>
                <IconMessagePlus size={16}/>
              </ActionIcon>
            </HasAccess>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <EntityCommentList objectId={settlement.id}/>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <CommentModal
        opened={opened}
        onClose={handlers.close}
        onSubmit={onCommentSubmit}
        loading={isCommentCreating}/>

      <CompanySettlementFormModal
        opened={truckingFormOpen}
        onClose={truckingFormHandler.close}
        onSubmit={onCompanySettlementFormSubmit}
        submitting={isCreating}
        options={{
          regionId: regionId,
          methodDisabled: true,
        }}
        defaultValues={{
          method: transportMethod.trucking,
        }}/>

      <CompanySettlementFormModal
        opened={pipelineFormOpen}
        onClose={pipelineFormHandler.close}
        onSubmit={onCompanySettlementFormSubmit}
        submitting={isCreating}
        options={{
          regionId: regionId,
          methodDisabled: true,
        }}
        defaultValues={{
          method: transportMethod.pipeline,
        }}/>
    </Grid>
  );
};
